interface NotificationActor {
  id: string
  name: string
  email: string
  app_label: string
  type: string
}

interface NotificationTarget {
  id: string
  name: string
  email?: string | null
  app_label: string
  type: string
}

interface NotificationData {
  lead_id?: string
  lead_name?: string
  new_status_label?: string
  old_status_label?: string
  // PROJECT_CREATED (ProjectNotificationService.notify_project_created)
  project_id?: string
  project_name?: string
  // Present on CALENDAR_EVENT_REMINDER (see calendars/notifications.py's
  // `_notify` helper) regardless of whether `target` itself is recognized —
  // the reliable way to build the calendar deep link.
  event_id?: string
  start_at?: string
  // Nullable on the model, and a newer WS field than start_at — absent on an
  // older backend or an event with no end time set.
  end_at?: string | null
}

type NotificationLevel = "success" | "info" | "warning" | "error" | number

const NOTIFICATION_TYPE = {
  PERMISSIONS: "permissions",
  // NotificationType.CALENDAR_EVENT_REMINDER = 111 (ezbuild_back
  // notifications_app/models.py). `type` (below) is the broad category
  // string ("calendar", "lead", ...) shared by EVERY notification of that
  // kind — a reminder, an assignment, a reschedule all carry `type:
  // "calendar"` — so this numeric code can only ever be compared against
  // `notification_name`, never `type`. Confirmed live: a real reminder's
  // `type` is literally the string "calendar", not 111 — a wrong assumption
  // here previously made the reminder toast/desktop-notification code an
  // unreachable branch against the real backend, passing only in unit tests
  // whose mock fixture set the wrong field's value.
  CALENDAR_EVENT_REMINDER: 111
} as const

type NotificationTypeKeys = keyof typeof NOTIFICATION_TYPE
type NotificationTypes = (typeof NOTIFICATION_TYPE)[NotificationTypeKeys]

interface Notification {
  id: number
  level: NotificationLevel
  verb: string
  description: string
  unread: boolean
  timestamp: string
  type?: NotificationTypes
  // The specific notification code (e.g. 111) — see NOTIFICATION_TYPE above
  // for why `type` cannot answer "which kind of calendar notification is
  // this". Not every notification sets it on the backend (only calendar-
  // category ones currently do), hence optional/nullable.
  notification_name?: number | null
  // Whether the row's own actor is a customer — not derivable from `type`
  // or `notification_name` alone, since a type like PROJECT_FILE_UPLOADED
  // fires for a staff upload just as often as a customer's. Absent on an
  // older backend.
  is_customer_activity?: boolean
  timesince: string
  actor: NotificationActor
  target: NotificationTarget
  data: NotificationData
  // Absent on an older backend.
  section?: NotificationSection
  group_count?: number
  // Sent only when the row stands for more than one update.
  group_members?: Notification[]
}

const NOTIFICATION_SECTION = {
  NEEDS_ATTENTION: "needs_attention",
  RECENT_UPDATES: "recent_updates"
} as const

type NotificationSection =
  (typeof NOTIFICATION_SECTION)[keyof typeof NOTIFICATION_SECTION]

type NotificationCursor = string | number | null

const WS_MESSAGE_TYPE = {
  INITIAL_STATE: "initial_state",
  NOTIFICATION_NEW: "notification.new",
  NOTIFICATIONS_BATCH: "notifications_batch",
  GET_ALL_MESSAGES: "get_all_messages",
  ERROR: "error",
  MARK_READ: "mark_read",
  MARK_READ_SUCCESS: "mark_read_success",
  BADGE_UPDATE: "notification.badge_update",
  FETCH_MORE: "fetch_more",
  ARCHIVE: "archive",
  ARCHIVE_SUCCESS: "archive_success",
  ARCHIVE_ALL_READ: "archive_all_read",
  ARCHIVE_ALL_READ_SUCCESS: "archive_all_read_success",
  // No longer offered in the interface; installed builds still send it.
  DELETE: "delete",
  PING: "ping"
} as const

type WSMessageTypeKeys = keyof typeof WS_MESSAGE_TYPE
type WSMessageType = (typeof WS_MESSAGE_TYPE)[WSMessageTypeKeys]

interface WSMessage {
  type: WSMessageType
  notifications?: Notification[]
  notification?: Notification
  unread_count?: number
  has_more?: boolean
  // An opaque position, not a row id. Older servers still send a number.
  next_cursor: NotificationCursor
}

interface NotificationSettingsItem {
  notification_type: number
  notification_type_name: string
  email: boolean
  in_app: boolean
  phone: boolean
  desktop: boolean
}

interface NotificationSettings {
  [key: string]: NotificationSettingsItem[]
}

export { WS_MESSAGE_TYPE, NOTIFICATION_TYPE, NOTIFICATION_SECTION }

export type {
  WSMessageType,
  NotificationLevel,
  NotificationSection,
  NotificationCursor,
  Notification,
  WSMessage,
  NotificationSettings,
  NotificationSettingsItem
}
